import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Cpu, RefreshCw } from 'lucide-react'
import { jimiService, type JimiObdHistoryRow, type JimiObdLatest } from '../services/jimiService'

// =============================================================================
// AssetObdWindow — engine telemetry for a JIMI GPS tracker carrying an OBD
// harness, scoped to a single asset.
// =============================================================================
//
// Layout:
//   - Top  : 3x6 grid of "metric tiles" populated from getObdLatest(). Each
//            tile shows label / value / unit and is tolerant of missing
//            metrics (vendor returns whichever the harness exposes).
//   - Bottom: paginated history table with date-range filter, calling
//            getObdHistory(). Columns are the same set of metrics; missing
//            cells render as "—".
//
// We deliberately keep the metric list complete (matching the user's
// requested set: ACC, Speed, Engine Speed, Fuel Level, Battery Voltage,
// Coolant Temp, Intake Air Temp, Throttle Position, Throttle Absolute
// Position, Manifold Pressure, MAF, Instant/Average/Total Fuel
// Consumption, Engine Runtime, Odometer, Cumulative Mileage, Vehicle
// Type) — even when no value is reported, the tile shows "No Data" so
// the operator knows the metric exists.
// =============================================================================

export interface AssetObdWindowProps {
  assetId: string
  imei: string
  deviceName?: string | null
  showMessage?: (msg: string, type: 'success' | 'error' | 'warning' | 'info') => void
}

interface MetricDef {
  key: keyof JimiObdLatest | string
  label: string
  unit?: string
  formatter?: (v: any) => string
}

function makeMetrics(t: (k: string, defaultValue?: string) => string): MetricDef[] {
  return [
    { key: 'acc',                       label: t('asset_obd_acc', 'ACC') },
    { key: 'speed',                     label: t('asset_obd_speed', 'Speed'), unit: 'km/h' },
    { key: 'engineSpeed',               label: t('asset_obd_engine_speed', 'Engine Speed'), unit: 'rpm' },
    { key: 'fuelLevel',                 label: t('asset_obd_fuel_level', 'Fuel Level'), unit: '%' },
    { key: 'batteryVoltage',            label: t('asset_obd_battery_voltage', 'Battery Voltage'), unit: 'V' },
    { key: 'coolantTemperature',        label: t('asset_obd_coolant_temp', 'Coolant Temperature'), unit: '°C' },
    { key: 'intakeAirTemperature',      label: t('asset_obd_intake_air_temp', 'Intake Air Temperature'), unit: '°C' },
    { key: 'throttlePosition',          label: t('asset_obd_throttle_pos', 'Throttle Position'), unit: '%' },
    { key: 'throttleAbsolutePosition',  label: t('asset_obd_throttle_abs_pos', 'Throttle Absolute Position'), unit: '%' },
    { key: 'manifoldPressure',          label: t('asset_obd_manifold_pressure', 'Manifold Pressure'), unit: 'kPa' },
    { key: 'massAirFlow',               label: t('asset_obd_mass_air_flow', 'Mass Air Flow'), unit: 'g/s' },
    { key: 'instantFuelConsumption',    label: t('asset_obd_instant_fuel', 'Instant Fuel Consumption'), unit: 'L/h' },
    { key: 'averageFuelConsumption',    label: t('asset_obd_avg_fuel', 'Average Fuel Consumption'), unit: 'L/100km' },
    { key: 'totalFuelConsumption',      label: t('asset_obd_total_fuel', 'Total Fuel Consumption'), unit: 'L' },
    { key: 'engineRuntime',             label: t('asset_obd_engine_runtime', 'Engine Runtime'), unit: 'h' },
    { key: 'odometer',                  label: t('asset_obd_odometer', 'Odometer'), unit: 'km' },
    { key: 'cumulativeMileage',         label: t('asset_obd_cum_mileage', 'Cumulative Mileage'), unit: 'km' },
    { key: 'vehicleType',               label: t('asset_obd_vehicle_type', 'Vehicle Type') },
  ]
}

function formatValue(v: any, unit?: string): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number') return unit ? `${v} ${unit}` : String(v)
  return String(v)
}

const AssetObdWindow: React.FC<AssetObdWindowProps> = ({
  assetId, imei, deviceName, showMessage,
}) => {
  const { t } = useTranslation()
  const metrics = useMemo(() => makeMetrics(t as any), [t])

  const [latest, setLatest] = useState<JimiObdLatest | null>(null)
  const [latestLoading, setLatestLoading] = useState(false)
  const [history, setHistory] = useState<JimiObdHistoryRow[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [from, setFrom] = useState('')
  const [to, setTo]     = useState('')
  const [page, setPage] = useState(1)
  const SIZE = 50

  const notify = useCallback((msg: string, type: 'success' | 'error' | 'warning' | 'info') => {
    if (showMessage) showMessage(msg, type)
    else if (type === 'error') console.warn(msg)
  }, [showMessage])

  const reloadLatest = useCallback(async () => {
    setLatestLoading(true)
    try {
      const data = await jimiService.getObdLatest(imei)
      setLatest(data || null)
    } catch (err: any) {
      notify(t('asset_obd_latest_error', 'Failed to load latest OBD: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setLatestLoading(false) }
  }, [imei, notify, t])

  const reloadHistory = useCallback(async () => {
    setHistoryLoading(true)
    try {
      const r = await jimiService.getObdHistory(imei, { from: from || undefined, to: to || undefined, page, size: SIZE })
      const list = (r as any)?.data?.records || r?.records || []
      setHistory(list)
    } catch (err: any) {
      notify(t('asset_obd_history_error', 'Failed to load OBD history: {{msg}}', { msg: err?.message || 'unknown' }), 'error')
    } finally { setHistoryLoading(false) }
  }, [imei, from, to, page, notify, t])

  useEffect(() => { reloadLatest() }, [reloadLatest])
  useEffect(() => { reloadHistory() }, [reloadHistory])

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-100 dark:bg-cyan-500/20 text-cyan-700 dark:text-cyan-300 flex items-center justify-center">
            <Cpu className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-sm">{deviceName || t('asset_obd_default_name', 'OBD Telemetry')}</div>
            <div className="text-xs text-gray-500 font-mono">IMEI {imei} · ASSET {assetId}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => { reloadLatest(); reloadHistory() }}
          className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 p-1.5"
          title={t('asset_obd_refresh', 'Refresh') as string}
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Live tiles */}
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            {t('asset_obd_live_section', 'Live snapshot')}
            {latestLoading && ` · ${t('loading', 'Loading…')}`}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {metrics.map((m) => {
              const raw = latest ? (latest as any)[m.key] : undefined
              const formatted = m.formatter ? m.formatter(raw) : formatValue(raw, m.unit)
              return (
                <div key={String(m.key)} className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <div className="text-[11px] text-gray-500">{m.label}</div>
                  <div className="text-sm font-medium mt-0.5">
                    {formatted === '—' ? <span className="text-gray-400">{t('asset_obd_no_data', 'No Data')}</span> : formatted}
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* History */}
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-500 mb-2 flex items-end gap-3">
            <span className="self-center">{t('asset_obd_history_section', 'History')}</span>
            <div>
              <label className="block text-[11px] text-gray-500">{t('asset_obd_from', 'From')}</label>
              <input
                type="datetime-local"
                value={from}
                onChange={(e) => { setFrom(e.target.value); setPage(1) }}
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>
            <div>
              <label className="block text-[11px] text-gray-500">{t('asset_obd_to', 'To')}</label>
              <input
                type="datetime-local"
                value={to}
                onChange={(e) => { setTo(e.target.value); setPage(1) }}
                className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
              />
            </div>
            <button
              type="button"
              onClick={reloadHistory}
              className="self-end px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              {t('asset_obd_apply', 'Apply')}
            </button>
          </div>

          {historyLoading && <div className="text-sm text-gray-500">{t('loading', 'Loading…')}</div>}
          {!historyLoading && history.length === 0 && (
            <div className="text-sm text-gray-500 italic">{t('asset_obd_history_empty', 'No OBD history rows in this range.')}</div>
          )}
          {!historyLoading && history.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-800/40">
                  <tr>
                    <th className="text-left px-2 py-1.5">{t('asset_obd_col_time', 'Time')}</th>
                    {metrics.map((m) => (
                      <th key={String(m.key)} className="text-left px-2 py-1.5 whitespace-nowrap">{m.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, idx) => (
                    <tr key={(row as any).id ?? idx} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="px-2 py-1 text-gray-500 whitespace-nowrap">{row.ts ? new Date(row.ts).toLocaleString() : '—'}</td>
                      {metrics.map((m) => (
                        <td key={String(m.key)} className="px-2 py-1 whitespace-nowrap">{formatValue((row as any)[m.key], m.unit)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex items-center justify-between mt-3">
            <button
              type="button"
              disabled={page <= 1 || historyLoading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="px-3 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {t('asset_obd_prev', 'Previous')}
            </button>
            <div className="text-xs text-gray-500">
              {t('asset_obd_page', 'Page {{page}}', { page })}
            </div>
            <button
              type="button"
              disabled={historyLoading || history.length < SIZE}
              onClick={() => setPage((p) => p + 1)}
              className="px-3 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
            >
              {t('asset_obd_next', 'Next')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AssetObdWindow
